import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router';
import { useAuth } from 'hooks/useAuth';
import { trackUmami } from '@/lib/umami/umami';
import { umamiAccountType } from '@/lib/umami/umami-events';
import { SigninScreen } from './SigninScreen';
import { VerifiedTakeover, type VerifyStatus } from './verify/VerifiedTakeover';

/**
 * /auth/verify is the sign-in screen with a full screen panel over it: confirming
 * the address happens on arrival, and the panel says how it went. "Sign in" slides
 * the panel back into its slot and the form is underneath, ready, so the way on from
 * being verified is one tap rather than a link to a different page.
 *
 * Closing lands on /auth/signin, replaced rather than pushed, so Back from the form
 * does not reopen a verification screen for a link that has already been used.
 */
export const VerifyScreen = () => {
  const [status, setStatus] = useState<VerifyStatus>('verifying');
  const { verify } = useAuth();
  const navigate = useNavigate();

  useEffect(() => {
    const verificationCode = new URL(window.location.toString()).searchParams.get('verificationCode');
    if (!verificationCode) {
      trackUmami('email-verify-failed');
      setStatus('failed');
      return;
    }

    verify(verificationCode)
      .then((result) => {
        setStatus('verified');
        // Absent when the link had already been used, so a second click is not counted.
        const type = umamiAccountType(result?.accountType);
        if (type) trackUmami('email-verified', { type });
      })
      .catch(() => {
        trackUmami('email-verify-failed');
        setStatus('failed');
      });
  }, []);

  return (
    <SigninScreen
      takeover={({ close }) => <VerifiedTakeover status={status} onContinue={close} />}
      onClosed={() => navigate('/auth/signin', { replace: true })}
    />
  );
};
